import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const GTMTracker = () => {
  const location = useLocation();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to push to dataLayer
  const pushToDataLayer = (data: any) => {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(data);
  };

  // 1. Page View Tracking (Virtual Page Views for SPA)
  useEffect(() => {
    pushToDataLayer({
      event: 'page_view',
      page_path: location.pathname + location.search,
      page_title: document.title,
    });
  }, [location]);

  // 2. Stay at frame > 6 sec (Engagement Timer)
  useEffect(() => {
    // Clear existing timer on location change
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Start new timer
    timerRef.current = setTimeout(() => {
      pushToDataLayer({
        event: 'frame_stay_6s',
        page_path: location.pathname,
      });
    }, 6000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [location]);

  // 3. Scroll Tracking
  useEffect(() => {
    const trackedPercentages = [25, 50, 75, 90];
    const tracked = new Set<number>();

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      // Prevent division by zero
      if (docHeight <= 0) return;
      
      const scrollPercent = (scrollTop / docHeight) * 100;

      trackedPercentages.forEach((p) => {
        if (scrollPercent >= p && !tracked.has(p)) {
          tracked.add(p);
          pushToDataLayer({
            event: 'scroll_tracking',
            scroll_depth: p,
            page_path: location.pathname,
          });
        }
      });
    };

    // Throttle helper could be added, but for simplicity relying on browser handling or light logic
    // Adding event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location]);

  // 4. Button Click Tracking (Global delegation)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      let target = e.target as HTMLElement;
      // Traverse up to find button or submit input
      while (target && target !== document.body) {
        const isButton = target.tagName === 'BUTTON';
        const isSubmitInput = target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'submit';
        // Some buttons might be anchors styled as buttons, but user said "every btn". 
        // We'll stick to semantic buttons for "btn", but maybe include anchors if they have "btn" class?
        // Let's stick to Semantic elements for reliability + simple <a> tracking if needed.
        // For now: Buttons and Inputs.
        
        if (isButton || isSubmitInput) {
             const btnText = target.innerText || (target as HTMLInputElement).value || target.getAttribute('aria-label') || '';
             const btnId = target.id;
             const btnClass = target.className;

             pushToDataLayer({
                event: 'custom_button_click',
                event_category: 'engagement', // Added for GA4 consistency
                event_label: 'button_click',  // Added for GA4 consistency
                button_text: btnText.substring(0, 100), // Limit length
                button_id: btnId,
                button_class: typeof btnClass === 'string' ? btnClass : '',
                page_path: location.pathname
             });
            break; // Handled
        }
        target = target.parentElement as HTMLElement;
      }
    };

    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [location]);

  return null;
};

export default GTMTracker;
